import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { SubmissionService } from '../../submission-data/submission.service';

import {
  PdfFileType,
  PdfUploadService,
  UploadedPdf,
} from './pdf-upload.service';

/**
 * Angular 12 port of the legacy `PDFUploadController`:
 *   - ui/app/components/efile/efileElements/EfileElementsController.js L1919-2116
 *   - ui/app/components/efile/efileElements/pdf-upload.html
 *
 * Responsibilities:
 *   1. Gate the whole surface behind the submission status — hide the
 *      upload form when `SUBMISSION_STATUS === 'Production Accepted'`
 *      (legacy template `ng-if="ctrl.SUBMISSION_STATUS !== ctrl.PRODUCTION_ACCEPTED"`).
 *   2. Load the two lookups on init: the file-type dropdown and the list
 *      of already uploaded PDFs for the active submission.
 *   3. Run the legacy `fileTypeChanged` logic (controller L2030-2088):
 *      - "Other" file type reveals an extra input
 *      - Selecting a standard type auto-fills `fileName` + `comments`
 *      - 8453SignatureDoc.pdf flips the `isSignatureDocument` flag and
 *        marks `comments` read-only
 *      - De-duplicates file names against the current grid
 *      - Truncates names longer than 65 chars
 *      - Asks the backend for the final file name
 *   4. Call `PdfUploadService.uploadPdf` on submit, with the legacy
 *      ".pdf" auto-append guard (controller L1969-1974).
 *   5. Expose per-row view/delete actions that spin an inline indicator
 *      while the action is in flight.
 */
@Component({
    selector: 'gtw-pdf-upload',
    templateUrl: './pdf-upload.component.html',
    styleUrls: ['./pdf-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PdfUploadComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();

  readonly PRODUCTION_ACCEPTED = 'Production Accepted';

  // Submission context
  submissionId: string | number = '';
  submissionStatus: string | null = null;
  taxYear: string | number = '';

  // Lookup + grid data
  fileTypeList: PdfFileType[] = [];
  uploadedPdfs: UploadedPdf[] = [];
  // Mirrors `uploadedPdfs` but narrowed by the toolbar search box. The
  // template binds against this array so changes to either the source
  // list or the search term trigger a fresh render under OnPush.
  filteredPdfs: UploadedPdf[] = [];
  searchQuery = '';
  loadingGrid = false;
  loadError: string | null = null;

  // Form state (names match the legacy vm.* fields where practical)
  isSignatureDocument = false;
  fileType = '';
  otherFileType = '';
  fileName = '';
  comments = '';
  pdfFile: File | null = null;
  showOtherField = false;
  readOnlyField = false;

  // Action state
  uploading = false;

  // Grid sort state. Mirrors the column "data" keys on the table so the
  // header click toggles asc / desc / unsorted. The legacy `gt-datatable`
  // exposed sortable headers out-of-the-box; the migrated static table
  // implements the same affordance manually.
  sortKey: string = '';
  sortDir: 'asc' | 'desc' = 'asc';

  @ViewChild('fileInput') fileInputRef: any;

  constructor(
    private cdr: ChangeDetectorRef,
    private pdfService: PdfUploadService,
    private snackbar: GtwSnackbarService,
    private appService: AppService,
    private submissionService: SubmissionService,
  ) {}

  ngOnInit(): void {
    const submission = this.submissionService.activeSubmission;
    if (submission) {
      this.submissionId = submission.SUBMISSION_ID;
      this.submissionStatus = (submission as any).SUBMISSION_STATUS || null;
      this.taxYear = submission.TAX_YEAR;
    }
    this.loadLookups();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  get canShowUploadForm(): boolean {
    return this.submissionStatus !== this.PRODUCTION_ACCEPTED;
  }

  get canUpload(): boolean {
    return !this.uploading && !!this.pdfFile && !!this.comments && !!this.fileName;
  }

  private baseUrls(): GtwElementsBaseUrlsModel {
    // AppService.baseURLs is populated on bootstrap. It only exposes the four
    // keys the service actively uses (api/custom/admin/efile); the extra keys
    // on GtwElementsBaseUrlsModel are tolerated as empty strings.
    const src = this.appService?.baseURLs || { api: '', custom: '', admin: '', efile: '' };
    return {
      api: src.api || '',
      admin: src.admin || '',
      custom: src.custom || '',
      efile: src.efile || '',
      pdfEngine: (src as any).pdfEngine || '',
      bulkPdfEngine: (src as any).bulkPdfEngine || '',
    };
  }

  private ssoId(): string {
    return this.appService.USER_SETTINGS?.user?.sso_id || '';
  }

  // ---------------------------------------------------------------------------
  // Lookups
  // ---------------------------------------------------------------------------

  loadLookups(): void {
    if (!this.submissionId) {
      this.loadError = 'No active submission selected';
      this.cdr.markForCheck();
      return;
    }
    const base = this.baseUrls();

    this.loadingGrid = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.subs.add(
      this.pdfService.loadFileTypes(base, this.ssoId()).subscribe({
        next: (response) => {
          this.fileTypeList = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackbar.openSnackBar('Failed to load file types', 'gtw-snackbar--red');
        },
      }),
    );

    this.subs.add(
      this.pdfService.loadUploadedPdfs(base, this.submissionId).subscribe({
        next: (response) => {
          this.uploadedPdfs = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
          this.applySort();
          this.loadingGrid = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingGrid = false;
          this.loadError = 'Failed to load uploaded PDFs';
          this.cdr.markForCheck();
        },
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Form interactions
  // ---------------------------------------------------------------------------

  onSignatureToggle(): void {
    // Legacy ng-change expression at pdf-upload.html L142:
    //   ctrl.fileType = ctrl.isSignatureDocument ? '8453SignatureDoc.pdf' : '';
    //   ctrl.fileName = ... same ...
    //   ctrl.comments = ctrl.isSignatureDocument ? '8453 Signature Document' : '';
    //   ctrl.fileTypeChanged(true)
    if (this.isSignatureDocument) {
      this.fileType = '8453SignatureDoc.pdf';
      this.fileName = '8453SignatureDoc.pdf';
      this.comments = '8453 Signature Document';
    } else {
      this.fileType = '';
      this.fileName = '';
      this.comments = '';
    }
    this.fileTypeChanged(true);
  }

  /**
   * Port of PDFUploadController.fileTypeChanged (controller L2030-2088).
   * The "Other" branch gets a little reorganised so the Angular template
   * renders deterministically, but the observable behaviour is preserved:
   *
   *   - Selecting "Other" shows the extra input and requires the user to
   *     type a value before we accept the change.
   *   - Any non-Other selection populates `comments` from the matching
   *     file-type NAME.
   *   - Selecting 8453SignatureDoc.pdf flips the signature flag + locks
   *     comments to the canonical label.
   *   - File names longer than 65 chars are trimmed (with a warning).
   *   - If the computed name already exists in the grid, append "(n).pdf".
   *   - Finally ask the backend for the canonical file name.
   */
  fileTypeChanged(isSignature = false): void {
    this.fileName = this.fileType || '';

    if (this.fileName === 'Other') {
      if (!this.otherFileType) {
        this.showOtherField = true;
        this.fileName = '';
        this.comments = '';
        this.isSignatureDocument = false;
        this.pdfFile = null;
        this.resetFileInput();
        this.cdr.markForCheck();
        return;
      }

      this.showOtherField = true;
      if (this.otherFileType.indexOf('&') !== -1) {
        this.snackbar.openSnackBar('Special character & is not allowed.', 'gtw-snackbar--red');
        return;
      }
      this.fileName = this.otherFileType;
      this.comments = this.otherFileType;
    } else {
      this.showOtherField = false;
      const match = this.fileTypeList.find((t) => t.ID === this.fileType);
      this.comments = match ? match.NAME : '';

      if (this.fileName === '8453SignatureDoc.pdf' || isSignature) {
        this.comments = '8453 Signature Document';
        this.isSignatureDocument = true;
        this.readOnlyField = true;
      } else {
        this.isSignatureDocument = false;
        this.readOnlyField = false;
      }
    }

    if (this.fileName.length > 65) {
      this.snackbar.openSnackBar(
        'Max length of filename is 65, trimming to 65.',
        'gtw-snackbar--yellow',
      );
      this.fileName = this.fileName.substr(0, 65);
    }

    // Legacy de-duplication (controller L2069-2073).
    if (this.uploadedPdfs.some((p) => p.FILE_NAME === this.fileName)) {
      const stem = this.fileName.replace('.pdf', '');
      const count = this.uploadedPdfs.filter((p) =>
        (p.FILE_NAME || '').includes(stem),
      ).length;
      this.fileName = `${stem}(${count + 1}).pdf`;
    }

    // Ask the backend for the canonical file name.
    if (this.fileName) {
      this.subs.add(
        this.pdfService
          .getPdfFileName(this.baseUrls(), this.submissionId, this.fileName)
          .subscribe({
            next: (response) => {
              if (String(response?.callSuccess) === '1' && response?.fileName) {
                this.fileName = response.fileName;
              } else if (response?.errorMessage) {
                this.snackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
              }
              this.cdr.markForCheck();
            },
            error: () => {
              this.snackbar.openSnackBar(
                'Something went wrong! Please try again.',
                'gtw-snackbar--red',
              );
            },
          }),
      );
    }

    this.cdr.markForCheck();
  }

  onOtherFileTypeChange(): void {
    this.fileTypeChanged();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pdfFile = input?.files?.length ? input.files[0] : null;
    this.cdr.markForCheck();
  }

  private resetFileInput(): void {
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  private clearInputs(): void {
    this.fileType = '';
    this.fileName = '';
    this.comments = '';
    this.otherFileType = '';
    this.isSignatureDocument = false;
    this.pdfFile = null;
    this.showOtherField = false;
    this.readOnlyField = false;
    this.resetFileInput();
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  uploadPdf(): void {
    if (!this.canUpload || !this.pdfFile) return;

    // Legacy ".pdf" enforcement (controller L1969-1974).
    if (this.fileName) {
      const tokens = this.fileName.split('.');
      const last = tokens.pop();
      if (!last || last.toLowerCase() !== 'pdf') {
        this.fileName = `${this.fileName}.pdf`;
      }
    }

    this.uploading = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.pdfService
        .uploadPdf(
          this.baseUrls(),
          this.submissionId,
          this.fileType,
          this.fileName,
          this.comments,
          this.pdfFile,
          this.isSignatureDocument,
        )
        .subscribe({
          next: (response) => {
            this.uploading = false;
            if (String(response?.callSuccess) === '1') {
              this.snackbar.openSnackBar(
                'The PDF was successfully uploaded!',
                'gtw-snackbar--green',
              );
              this.clearInputs();
              this.loadLookups();
            } else {
              this.snackbar.openSnackBar(
                response?.errorMessage || 'Upload failed',
                'gtw-snackbar--red',
              );
              this.cdr.markForCheck();
            }
          },
          error: () => {
            this.uploading = false;
            this.snackbar.openSnackBar(
              'Something went wrong! Please try again.',
              'gtw-snackbar--red',
            );
            this.clearInputs();
          },
        }),
    );
  }

  viewPdf(row: UploadedPdf): void {
    if (row.loadingPdf) return;
    row.loadingPdf = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.pdfService.viewPdf(this.baseUrls(), this.submissionId, row.FILE_NAME).subscribe({
        next: (buffer: ArrayBuffer) => {
          row.loadingPdf = false;
          try {
            const blob = new Blob([buffer], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(
              url,
              '_blank',
              `toolbar=0,location=0,menubar=0,height=${window.outerHeight}`,
            );
          } catch {
            this.snackbar.openSnackBar('Unable to open PDF', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
        error: () => {
          row.loadingPdf = false;
          this.snackbar.openSnackBar(
            'Something went wrong! Please try again.',
            'gtw-snackbar--red',
          );
          this.cdr.markForCheck();
        },
      }),
    );
  }

  deletePdf(row: UploadedPdf): void {
    if (row.loadingPdf) return;
    row.loadingPdf = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.pdfService.deletePdf(this.baseUrls(), this.submissionId, row.FILE_NAME).subscribe({
        next: (response) => {
          row.loadingPdf = false;
          if (String(response?.callSuccess) === '1') {
            this.snackbar.openSnackBar(
              'The PDF was successfully deleted!',
              'gtw-snackbar--green',
            );
            this.loadLookups();
          } else {
            this.snackbar.openSnackBar(
              response?.errorMessage || 'Delete failed',
              'gtw-snackbar--red',
            );
            this.cdr.markForCheck();
          }
        },
        error: () => {
          row.loadingPdf = false;
          this.snackbar.openSnackBar(
            'Something went wrong! Please try again.',
            'gtw-snackbar--red',
          );
          this.cdr.markForCheck();
        },
      }),
    );
  }

  trackByRow(_index: number, row: UploadedPdf): string {
    return row.FILE_NAME;
  }

  // ---------------------------------------------------------------------------
  // Sorting
  // ---------------------------------------------------------------------------

  /**
   * Header click toggles between asc / desc on the same column, or
   * activates the new column at asc. Re-runs the sort against the
   * currently-loaded `uploadedPdfs` so we never round-trip to the
   * backend (the legacy gt-datatable also sorted in-memory).
   */
  toggleSort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.applySort();
    this.cdr.markForCheck();
  }

  sortIconClass(key: string): string {
    if (this.sortKey !== key) return 'fa fa-sort';
    return this.sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
  }

  private applySort(): void {
    if (this.sortKey) {
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      this.uploadedPdfs = [...this.uploadedPdfs].sort((a: any, b: any) => {
        const av = a?.[key];
        const bv = b?.[key];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        // Use Date semantics for the create-date column when both values
        // parse as valid dates (legacy controller stored a millis string).
        if (key === 'FORMATTED_CREATE_DATE') {
          const ad = Date.parse(av);
          const bd = Date.parse(bv);
          if (!isNaN(ad) && !isNaN(bd)) return (ad - bd) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
    this.applyFilter();
  }

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------

  /**
   * Re-runs the in-memory filter against the currently loaded `uploadedPdfs`.
   * Matches the searchable columns (file name, comments, created by, created
   * date) case-insensitively. Called from `applySort` so any data refresh or
   * column re-sort transparently keeps `filteredPdfs` in sync.
   */
  private applyFilter(): void {
    const query = (this.searchQuery || '').trim().toLowerCase();
    if (!query) {
      this.filteredPdfs = [...this.uploadedPdfs];
      return;
    }
    this.filteredPdfs = this.uploadedPdfs.filter((row: any) => {
      const fields = [
        row?.FILE_NAME,
        row?.COMMENTS,
        row?.USER_NAME,
        row?.FORMATTED_CREATE_DATE,
      ];
      return fields.some(
        (value) => value != null && String(value).toLowerCase().includes(query),
      );
    });
  }

  onSearchChange(value: string): void {
    this.searchQuery = value || '';
    this.applyFilter();
    this.cdr.markForCheck();
  }

  // Production-accepted guard on per-row delete action (template parity with
  // the legacy `ng-if` at pdf-upload.html L1941).
  canDeleteRows(): boolean {
    return this.submissionStatus !== this.PRODUCTION_ACCEPTED;
  }
}
